import { useEffect, useMemo, useState } from "react";
import { useAccessStore } from "../lib/store/useAccessStore";

type WebPlatform = "ios" | "android" | "pc";

type UtmParams = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
};

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
] as const;

const UTM_DEFAULTS: Partial<Record<(typeof UTM_KEYS)[number], string>> = {
  utm_source: "direct",
  utm_medium: "organic",
  utm_campaign: "general",
};

/* App Open / Deep Link
 * PC  → 새 탭으로 앱 웹 페이지 오픈
 * AOS → intent:// + 커스텀 스킴 (앱 미설치 시 Play Store 자동 fallback)
 * iOS → "앱 열기" / "앱 설치하기" 두 버튼
 *       앱 열기: Universal Link로 같은 페이지(/app-open?_app_install_alert=1) 이동
 *               앱 설치 O → iOS가 가로채 앱 실행 (페이지 navigation 취소)
 *               앱 설치 X → 같은 모달 페이지가 reload되며 쿼리 감지 → "설치해주세요" 얼럿
 *       앱 설치하기: App Store 바로 이동
 *
 * UTM (optional): utm_source/utm_medium/utm_campaign/utm_content
 *   스토어 이동 경로에만 적용 (앱 실행 / PC 회원가입 경로에는 전달하지 않음)
 *   AOS: 미설치 fallback Play Store URL 의 referrer 에 URL 인코딩하여 포함
 *   iOS: App Store ct 파라미터에 값을 언더스코어(_)로 연결한 단일 토큰으로 포함
 *   각 키 미지정 시 기본값으로 보정 (source=direct / medium=organic / campaign=general,
 *   content는 기본값 없음)
 */
const APP_CUSTOM_SCHEME = "voicepark-app://";
const APP_AOS_STORE =
  "https://play.google.com/store/apps/details?id=com.fixelsoft.voicepark";
const APP_IOS_STORE = "https://apps.apple.com/app/id6749922967";
const APP_IOS_STORE_WITH_UTM_BASE =
  "https://apps.apple.com/kr/app/id6749922967";
const APP_IOS_AFFILIATE_TOKEN = "voicepark";
const APP_WEB_HOST = "www.vcparkgolf.com";
const APP_SIGNUP_PATH = "/signup";
const APP_OPEN_PATH = "/app-open";
const APP_INSTALL_ALERT_QUERY = "_app_install_alert";

const APP_NOT_INSTALLED_ALERT = "앱을 먼저 설치해주세요.";

function detectWebPlatform(): WebPlatform {
  const ua = navigator.userAgent;
  if (/iP(hone|od|ad)/.test(ua)) return "ios";
  if (/Android/.test(ua)) return "android";
  return "pc";
}

function extractUtmFromUrl(): UtmParams {
  const params = new URLSearchParams(window.location.search);
  const utm: UtmParams = {};
  for (const key of UTM_KEYS) {
    const value = params.get(key) ?? UTM_DEFAULTS[key];
    if (value) utm[key] = value;
  }
  return utm;
}

function buildUtmQueryString(utm: UtmParams): string {
  return UTM_KEYS.filter((key) => utm[key])
    .map((key) => `${key}=${utm[key]}`)
    .join("&");
}

function buildIosCtToken(utm: UtmParams): string {
  return UTM_KEYS.filter((key) => utm[key])
    .map((key) => utm[key])
    .join("_");
}

function buildAndroidStoreFallbackUrl(utm: UtmParams): string {
  const utmString = buildUtmQueryString(utm);
  if (!utmString) return APP_AOS_STORE;
  return APP_AOS_STORE + "&referrer=" + encodeURIComponent(utmString);
}

function buildIosStoreUrl(utm: UtmParams): string {
  const ctToken = buildIosCtToken(utm);
  if (!ctToken) return APP_IOS_STORE;
  return (
    APP_IOS_STORE_WITH_UTM_BASE +
    "?pt=" +
    APP_IOS_AFFILIATE_TOKEN +
    "&ct=" +
    encodeURIComponent(ctToken) +
    "&mt=8"
  );
}

function resolvePcWebTargetPath(): string {
  const isAuthed = useAccessStore.getState().isAuthenticated();
  if (!isAuthed) return APP_SIGNUP_PATH;
  const urlParam = new URLSearchParams(window.location.search).get("url");
  if (!urlParam) return "/";
  return urlParam.startsWith("/") ? urlParam : "/" + urlParam;
}

function openPcWeb() {
  window.open("https://" + APP_WEB_HOST + resolvePcWebTargetPath(), "_blank");
}

function buildAppOpenPathWithUrlParam(): string {
  const urlParam = new URLSearchParams(window.location.search).get("url");
  if (!urlParam) return APP_OPEN_PATH;
  return APP_OPEN_PATH + "?url=" + encodeURIComponent(urlParam);
}

function openAndroidApp(utm: UtmParams) {
  const schemeName = APP_CUSTOM_SCHEME.replace("://", "");
  const fallbackUrl = buildAndroidStoreFallbackUrl(utm);
  window.location.href =
    "intent://" +
    APP_WEB_HOST +
    buildAppOpenPathWithUrlParam() +
    "#Intent;scheme=" +
    schemeName +
    ";package=com.fixelsoft.voicepark" +
    ";S.browser_fallback_url=" +
    encodeURIComponent(fallbackUrl) +
    ";end";
}

function goToIosStore(utm: UtmParams) {
  window.location.href = buildIosStoreUrl(utm);
}

export default function AppOpen() {
  const [isOpen, setIsOpen] = useState(true);

  const platform = useMemo(detectWebPlatform, []);
  const utm = useMemo(extractUtmFromUrl, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get(APP_INSTALL_ALERT_QUERY) !== "1") return;

    const url = new URL(window.location.href);
    url.searchParams.delete(APP_INSTALL_ALERT_QUERY);
    window.history.replaceState({}, "", url.toString());

    window.alert(APP_NOT_INSTALLED_ALERT);
    window.location.href = buildIosStoreUrl(utm);
  }, [utm]);

  if (!isOpen) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white">
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          className="rounded-[10px] bg-primary px-6 py-3 text-white"
        >
          모달 다시 열기
        </button>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
      <div
        className="flex flex-col items-center justify-between bg-white shadow-lg py-4 px-2"
        style={{
          width: "90vw",
          borderRadius: "2vmin",
        }}
      >
        <div className="flex flex-1 flex-col items-center justify-center text-center mb-4">
          <h2
            className="font-semibold text-[#222] whitespace-pre-line"
            style={{
              fontSize: "6vmin",
              marginBottom: "4vmin",
              lineHeight: 1.4,
              wordBreak: "keep-all",
            }}
          >
            파크골프 대회 접수는
            <br />
            <span
              className="underline text-primary"
              style={{
                fontSize: "8vmin",
              }}
            >
              보이스파크 앱에서
            </span>
          </h2>
          <p
            className="text-[#222]"
            style={{ fontSize: "6vmin", lineHeight: 1.5 }}
          >
            회원가입 한 번으로
            <br />
            대회 신청부터 스코어 관리까지
          </p>
        </div>

        {platform === "ios" ? (
          <button
            type="button"
            onClick={() => goToIosStore(utm)}
            className="w-[90%] bg-primary font-medium text-white py-2"
            style={{
              borderRadius: "1.5vmin",
              fontSize: "6vmin",
            }}
          >
            가입하기
          </button>
        ) : (
          <button
            type="button"
            onClick={platform === "pc" ? openPcWeb : () => openAndroidApp(utm)}
            className="w-[90%] bg-primary font-medium text-white py-2"
            style={{
              borderRadius: "1.5vmin",
              fontSize: "6vmin",
            }}
          >
            {platform === "pc" ? "가입하기" : "앱 설치 또는 실행하기"}
          </button>
        )}
        <img
          src="/icons/ic-bi-ko.png"
          alt="보이스파크"
          className="mt-4"
          style={{
            height: "5vmin",
            marginBottom: "3vmin",
            objectFit: "contain",
          }}
        />
      </div>
    </div>
  );
}
